import { useEffect, useState } from 'react';
import { nip19 } from 'nostr-tools';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Shorten an npub for display: npub1abc…wxyz. */
export function shortNpub(npub: string): string {
  return npub.length > 18 ? `${npub.slice(0, 12)}…${npub.slice(-4)}` : npub;
}

export function pubkeyToNpub(pubkey: string): string | null {
  try {
    return nip19.npubEncode(pubkey);
  } catch {
    return null;
  }
}

/**
 * Clickable npub under an author's name. Copies the full npub on click and
 * flashes a small green check that fades away.
 */
export function CopyNpub({
  pubkey,
  className,
  textClassName,
}: {
  pubkey: string;
  className?: string;
  textClassName?: string;
}) {
  const [copied, setCopied] = useState(false);
  const npub = pubkeyToNpub(pubkey);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (!npub) return null;

  const copy = async (e: React.MouseEvent) => {
    // Don't trigger parent links (pack cards wrap everything in a <Link>).
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(npub);
    } catch {
      // Clipboard API can fail on insecure contexts — still show feedback.
    }
    setCopied(true);
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy ${npub}`}
      aria-label={`Copy creator npub ${npub}`}
      className={cn(
        'group/npub inline-flex min-w-0 items-center gap-1 rounded font-mono text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        className,
      )}
    >
      <span className={cn('truncate', textClassName)}>{shortNpub(npub)}</span>
      <span
        aria-hidden={!copied}
        className={cn(
          'inline-flex shrink-0 items-center transition-all motion-safe:duration-500',
          copied ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
        )}
      >
        <Check className="size-3.5 text-green-600 dark:text-green-400" strokeWidth={3} />
      </span>
      <span className="sr-only" aria-live="polite">
        {copied ? 'Copied!' : ''}
      </span>
    </button>
  );
}
